import type { HTMLAttributes, ReactNode, Ref } from "react";
import { Radio as BaseRadio } from "@base-ui/react/radio";
import { RadioGroup as BaseRadioGroup } from "@base-ui/react/radio-group";
import classNames from "classnames";
import { ariaLabelledbyProps } from "@design-system/formControl/ariaLabelledbyProps";
import styles from "./Radio.module.scss";

// Visual source of truth: docs/specs/2026-08-26-radio-figma-rework.md.
// Base UI renders a hidden input sibling after each root, so positional radio
// selectors must use :not(:first-child), never the adjacent-sibling combinator.

interface RadioGroupProps<T extends string = string> {
    name?: string;
    /** Names the group for assistive tech when no visible heading labels it. */
    "aria-label"?: string;
    /** Id of a visible element elsewhere that names the group. */
    "aria-labelledby"?: string;
    /** `null` selects no radio. */
    value: T | null;
    /** Omitted by read-only groups that render a fixed selection. */
    onValueChange?: (value: NoInfer<T>) => void;
    /** Fires when focus leaves the group, not when it moves between radios. */
    onBlur?: () => void;
    /** Sets `aria-invalid` on the group. */
    invalid?: boolean;
    disabled?: boolean;
    readOnly?: boolean;
    /** Announced as `aria-required`; native `required` would block form submit. */
    required?: boolean;
    className?: string;
    children?: ReactNode;
    ref?: Ref<HTMLDivElement>;
}

export const RadioGroup = <T extends string = string>({
    name,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledby,
    value,
    onValueChange,
    onBlur,
    invalid = false,
    disabled = false,
    readOnly = false,
    required = false,
    className,
    children,
    ref,
}: Readonly<RadioGroupProps<T>>) => (
    <BaseRadioGroup<T | null>
        ref={ref}
        name={name}
        aria-label={ariaLabel}
        {...ariaLabelledbyProps(ariaLabelledby)}
        aria-invalid={invalid || undefined}
        aria-required={required || undefined}
        value={value}
        onValueChange={(nextValue) => {
            if (nextValue !== null) {
                onValueChange?.(nextValue);
            }
        }}
        onBlur={(event) => {
            if (
                !(event.relatedTarget instanceof Node) ||
                !event.currentTarget.contains(event.relatedTarget)
            ) {
                onBlur?.();
            }
        }}
        disabled={disabled}
        readOnly={readOnly}
        className={classNames(styles.group, className)}
    >
        {children}
    </BaseRadioGroup>
);

/**
 * DOM props a wrapping trigger (tooltip, popover) clones onto its child: event
 * handlers and attributes land on the focusable root.
 */
type RadioRootDomProps = Omit<
    HTMLAttributes<HTMLElement>,
    "children" | "className" | "role" | "style" | `aria-${string}`
>;

interface RadioProps<T extends string = string> extends RadioRootDomProps {
    value: T;
    label?: ReactNode;
    disabled?: boolean;
    className?: string;
    /** Playground-only: statically render an interaction state. */
    "data-forced-state"?: "hover" | "pressed" | "focus";
    ref?: Ref<HTMLSpanElement>;
}

export const Radio = <T extends string = string>({
    value,
    label,
    disabled = false,
    className,
    "data-forced-state": forcedState,
    ref,
    ...rootDomProps
}: Readonly<RadioProps<T>>) => (
    <BaseRadio.Root
        {...rootDomProps}
        ref={ref}
        value={value}
        disabled={disabled}
        className={classNames(styles.radio, className)}
        data-forced-state={forcedState}
    >
        {/* The whole row is the Base UI root, so a label click selects and the
                accessible name comes from the same element. */}
        <span className={styles.control} />
        {label !== undefined && <span className={styles.label}>{label}</span>}
    </BaseRadio.Root>
);
